import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import * as api from '../../api';

interface ShareTripDialogProps {
  open: boolean;
  onClose: () => void;
  tripId: number;
  tripName?: string;
}

function ShareTripDialog({ open, onClose, tripId, tripName }: ShareTripDialogProps) {
  const [email, setEmail] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    setEmail('');
    setCanEdit(false);
    setError(null);
    setLoading(false);
    setSuccess(false);
    onClose();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await api.shareTrip({
        trip_id: tripId,
        email: email.trim(),
        can_edit: canEdit,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share trip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight={600}>
            {success ? 'Trip Shared!' : 'Share Trip'}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Invitation Sent!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We've sent an invitation to <strong>{email}</strong> to {canEdit ? 'edit' : 'view'} this trip.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {tripName && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Sharing
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {tripName}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address to share with"
              disabled={loading}
              autoFocus
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Permission
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <Box
                  onClick={() => !loading && setCanEdit(false)}
                  sx={{
                    flex: 1,
                    p: 2,
                    border: '2px solid',
                    borderColor: !canEdit ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    cursor: loading ? 'default' : 'pointer',
                    bgcolor: !canEdit ? 'primary.50' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: loading ? undefined : 'primary.main',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <VisibilityIcon sx={{ fontSize: 20, color: !canEdit ? 'primary.main' : 'grey.500' }} />
                    <Typography variant="body2" fontWeight={600} color={!canEdit ? 'primary.main' : 'text.primary'}>
                      View Only
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Can view the trip but cannot make changes
                  </Typography>
                </Box>
                <Box
                  onClick={() => !loading && setCanEdit(true)}
                  sx={{
                    flex: 1,
                    p: 2,
                    border: '2px solid',
                    borderColor: canEdit ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    cursor: loading ? 'default' : 'pointer',
                    bgcolor: canEdit ? 'primary.50' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: loading ? undefined : 'primary.main',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <EditIcon sx={{ fontSize: 20, color: canEdit ? 'primary.main' : 'grey.500' }} />
                    <Typography variant="body2" fontWeight={600} color={canEdit ? 'primary.main' : 'text.primary'}>
                      Can Edit
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Can view and make changes to the trip
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" color="primary">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
            >
              {loading ? 'Sharing...' : 'Share Trip'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ShareTripDialog;
